import { useCallback, useMemo } from 'react';
import { useCategoryStockData } from './useCategoryStockData';
import { useFinancialMetrics } from './useFinancialMetrics';
import { useHints } from './useHints';
import { useCheckGamePlayed } from './useCheckGamePlayed';
import type { HintData, StockData, ChartDataPoint } from '../types/game';
import type { StockCategoryFilter } from '../types/stockCategories';
import { getCurrentGameTimestamp } from '../config/dateTimeConfig';

type StepStatus = 'idle' | 'loading' | 'ready' | 'error';

type BootstrapStepMap = {
  stock: StepStatus;
  metrics: StepStatus;
  hints: StepStatus;
  cache: StepStatus;
};

export interface CategoryDailySnapshot {
  category: StockCategoryFilter;
  date: string;
  fetchedAt: string;
  stock: StockData | null;
  stockSource: 'cache' | 'api' | null;
  chartData: ChartDataPoint[];
  metrics: ReturnType<typeof useFinancialMetrics>['metrics'];
  rawMetrics: ReturnType<typeof useFinancialMetrics>['rawMetrics'];
  hints: HintData[];
}

interface UseCategoryBootstrapOptions {
  category: StockCategoryFilter;
  disabled?: boolean;
  date?: string;
  isReadOnly?: boolean;
}

export interface CategoryBootstrapState {
  isLoading: boolean;
  isPageReady: boolean;
  hasStock: boolean;
  hasChart: boolean;
  hasMetrics: boolean;
  hasHints: boolean;
  isAwaitingHints: boolean;
  isHydratingFromCache: boolean;
  steps: BootstrapStepMap;
  error: string | null;
  errors: {
    stock: string | null;
    metrics: string | null;
    hints: string | null;
  };
  lastUpdatedAt: string | null;
  hasPlayedToday: boolean;
  playedGameData: {
    won: boolean;
    guesses: number;
    timeMs: number;
  } | null;
}

export interface UseCategoryBootstrapReturn {
  bootstrapState: CategoryBootstrapState;
  stock: ReturnType<typeof useCategoryStockData> & {
    hasLiveData: boolean;
  };
  metrics: ReturnType<typeof useFinancialMetrics> & {
    hasLiveData: boolean;
  };
  hints: ReturnType<typeof useHints> & {
    hasLiveData: boolean;
  };
  snapshot: CategoryDailySnapshot | null;
  refetchAll: () => Promise<void>;
}

const deriveStepStatus = (
  loading: boolean,
  error: string | null | undefined,
  hasData: boolean
): StepStatus => {
  if (loading) return 'loading';
  if (error && !hasData) return 'error';
  if (hasData) return 'ready';
  return 'idle';
};

export function useCategoryBootstrap(
  options: UseCategoryBootstrapOptions
): UseCategoryBootstrapReturn {
  const { category, disabled = false, date, isReadOnly = false } = options;

  // Get today's game date (respects the configured reset time boundary)
  const today = getCurrentGameTimestamp();
  const effectiveDate = date || today;

  // Check if user has already played this category today
  // Skip check for read-only pages (e.g., yesterday page)
  const checkPlayedHook = useCheckGamePlayed({
    category,
    gameDate: effectiveDate,
    enabled: !disabled && !isReadOnly
  });

  const stockHook = useCategoryStockData({
    category,
    autoFetch: !disabled,
    date
  });
  const metricsHook = useFinancialMetrics({
    category,
    autoFetch: !disabled,
    date
  });
  const hintsHook = useHints({
    category,
    autoFetch: !disabled,
    date
  });

  const hasStock = Boolean(stockHook.stockData);
  const hasChart = stockHook.chartData.length > 0;
  const hasMetrics = metricsHook.hasData;
  const hasHints = hintsHook.hints.length > 0;

  const errors = useMemo(
    () => ({
      stock: stockHook.error,
      metrics: metricsHook.error,
      hints: hintsHook.error
    }),
    [stockHook.error, metricsHook.error, hintsHook.error]
  );

  const blockingError = useMemo(() => {
    if (!hasStock && errors.stock) return errors.stock;
    if (!hasMetrics && errors.metrics) return errors.metrics;
    return null;
  }, [errors, hasStock, hasMetrics]);

  const isLoadingAny =
    !disabled && (stockHook.isLoading || metricsHook.isLoading || hintsHook.isLoading || checkPlayedHook.loading);

  const isPageReady =
    !disabled && hasStock && hasChart && hasMetrics && !blockingError && !checkPlayedHook.loading;

  const bootstrapState: CategoryBootstrapState = useMemo(() => {
    const steps: BootstrapStepMap = {
      stock: deriveStepStatus(stockHook.isLoading, errors.stock, hasStock && hasChart),
      metrics: deriveStepStatus(metricsHook.isLoading, errors.metrics, hasMetrics),
      hints: deriveStepStatus(hintsHook.isLoading, errors.hints, hasHints),
      cache: 'idle'
    };

    return {
      isLoading: !disabled && !isPageReady && isLoadingAny,
      isPageReady,
      hasStock,
      hasChart,
      hasMetrics,
      hasHints,
      isAwaitingHints: !hasHints,
      isHydratingFromCache: false,
      steps,
      error: blockingError,
      errors,
      lastUpdatedAt: null,
      hasPlayedToday: checkPlayedHook.hasPlayed,
      playedGameData: checkPlayedHook.playData ? {
        won: checkPlayedHook.playData.won,
        guesses: checkPlayedHook.playData.guesses,
        timeMs: checkPlayedHook.playData.timeMs
      } : null
    };
  }, [
    blockingError,
    disabled,
    errors,
    hasChart,
    hasHints,
    hasMetrics,
    hasStock,
    hintsHook.isLoading,
    isLoadingAny,
    isPageReady,
    metricsHook.isLoading,
    stockHook.isLoading,
    checkPlayedHook.hasPlayed,
    checkPlayedHook.playData
  ]);

  const refetchAll = useCallback(async () => {
    const results = await Promise.allSettled([
      stockHook.refetch(),
      metricsHook.refetch(),
      hintsHook.refetch()
    ]);

    const rejected = results.find(
      (result): result is PromiseRejectedResult => result.status === 'rejected'
    );

    if (rejected) {
      throw rejected.reason;
    }
  }, [hintsHook, metricsHook, stockHook]);

  const stockState = useMemo(
    () => ({
      ...stockHook,
      hasLiveData: Boolean(stockHook.stockData)
    }),
    [stockHook]
  );

  const metricsState = useMemo(
    () => ({
      ...metricsHook,
      hasLiveData: metricsHook.hasData
    }),
    [metricsHook]
  );

  const hintsState = useMemo(
    () => ({
      ...hintsHook,
      hasLiveData: hintsHook.hints.length > 0
    }),
    [hintsHook]
  );

  return {
    bootstrapState,
    stock: stockState,
    metrics: metricsState,
    hints: hintsState,
    snapshot: null,
    refetchAll
  };
}

export default useCategoryBootstrap;
